const DESKTOP_MQ = window.matchMedia('(min-width: 1024px)');
const MOBILE_MQ = window.matchMedia('(max-width: 1023.98px)');
const CLOSE_DELAY_MS = 150;
const ARROW_TAP_SLOP_PX = 24;

const FOCUSABLE_SELECTOR = [
	'a[href]',
	'area[href]',
	'button:not([disabled])',
	'input:not([disabled]):not([type="hidden"])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'iframe',
	'[tabindex]:not([tabindex="-1"])',
	'[contenteditable="true"]',
].join(',');

function isVisible(el) {
	return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
}

function hasDestination(link) {
	const href = (link.getAttribute('href') || '').trim();
	return href !== '' && !href.startsWith('#');
}

function isArrowTap(link, event) {
	if (event.target.closest('.pmm-arrow')) return true;
	const arrow = link.querySelector('.pmm-arrow');
	if (!arrow) return false;
	return event.clientX >= arrow.getBoundingClientRect().left - ARROW_TAP_SLOP_PX;
}

function closeItem(item) {
	if (!item) return;
	item.classList.remove('pmm-open');
	item.style.removeProperty('--pmm-height');
	const link = item.querySelector('.pmm-menu-link');
	const dropdown = item.querySelector('.pmm-mega-dropdown');
	if (link) link.setAttribute('aria-expanded', 'false');
	if (dropdown) dropdown.setAttribute('hidden', '');
}

export default class MegaMenu {
	static init() {
		const headerMain = document.querySelector('.header-main');

		if (!headerMain) {
			return;
		}

		const items = headerMain.querySelectorAll('.pmm-has-megamenu');

		if (items.length) {
			MegaMenu.bindItems(items, headerMain);
			MegaMenu.bindOutsideClose(items);
			MegaMenu.bindHeightSync(headerMain, items);
		}

		MegaMenu.bindMobileDrawer(headerMain);
	}

	static bindItems(items, headerMain) {
		items.forEach((item) => {
			const link = item.querySelector('.pmm-menu-link');
			const dropdown = item.querySelector('.pmm-mega-dropdown');

			if (!link || !dropdown) return;

			let closeTimer;

			const open = () => {
				window.clearTimeout(closeTimer);
				items.forEach((other) => {
					if (other !== item) closeItem(other);
				});
				dropdown.removeAttribute('hidden');
				// Setting --pmm-height synchronously prevents a hover-flicker where the
				// header would briefly overlap the still-collapsed panel and fire
				// mouseleave on the trigger.
				if (DESKTOP_MQ.matches) {
					headerMain.style.setProperty('--pmm-height', `${dropdown.scrollHeight}px`);
				} else {
					item.style.setProperty('--pmm-height', `${dropdown.scrollHeight}px`);
				}
				item.classList.add('pmm-open');
				link.setAttribute('aria-expanded', 'true');
			};

			const scheduleClose = () => {
				window.clearTimeout(closeTimer);
				closeTimer = window.setTimeout(() => {
					// The panel is a descendant of item, so :hover here covers a pointer
					// that landed on either the trigger or the panel while the delay ran.
					if (item.matches(':hover')) return;
					closeItem(item);
				}, CLOSE_DELAY_MS);
			};

			item.addEventListener('mouseenter', () => {
				if (!DESKTOP_MQ.matches) return;
				open();
			});
			item.addEventListener('mouseleave', () => {
				if (!DESKTOP_MQ.matches) return;
				scheduleClose();
			});
			dropdown.addEventListener('mouseenter', () => window.clearTimeout(closeTimer));
			dropdown.addEventListener('mouseleave', () => {
				if (!DESKTOP_MQ.matches) return;
				scheduleClose();
			});

			link.addEventListener('click', (event) => {
				const isKeyboardActivation = event.detail === 0;
				const isMobileLabelTap = !DESKTOP_MQ.matches
					&& !isKeyboardActivation
					&& hasDestination(link)
					&& !isArrowTap(link, event);

				if (isMobileLabelTap) return;

				if (!DESKTOP_MQ.matches || isKeyboardActivation) {
					event.preventDefault();
					if (item.classList.contains('pmm-open')) {
						closeItem(item);
					} else {
						open();
					}
				}
			});

			item.addEventListener('keydown', (event) => {
				if (event.key === 'Escape') {
					closeItem(item);
					link.focus();
				}
			});
		});
	}

	static bindOutsideClose(items) {
		document.addEventListener('click', (event) => {
			if (event.target.closest('.pmm-has-megamenu')) {
				return;
			}
			items.forEach((item) => closeItem(item));
		});
	}

	static bindHeightSync(headerMain, items) {
		const syncHeight = () => {
			if (!DESKTOP_MQ.matches) {
				headerMain.style.removeProperty('--pmm-height');
				return;
			}

			const openItem = headerMain.querySelector('.pmm-has-megamenu.pmm-open');
			const dropdown = openItem?.querySelector('.pmm-mega-dropdown');

			if (!dropdown || dropdown.hasAttribute('hidden')) {
				headerMain.style.setProperty('--pmm-height', '0px');
				return;
			}

			headerMain.style.setProperty('--pmm-height', `${dropdown.scrollHeight}px`);
		};

		const scheduleSync = () => {
			window.requestAnimationFrame(() => {
				window.requestAnimationFrame(syncHeight);
			});
		};

		items.forEach((item) => {
			const dropdown = item.querySelector('.pmm-mega-dropdown');

			if (!dropdown) {
				return;
			}

			const observer = new MutationObserver(scheduleSync);

			observer.observe(item, { attributes: true, attributeFilter: ['class'] });
			observer.observe(dropdown, { attributes: true, attributeFilter: ['hidden'] });
		});

		window.addEventListener('resize', scheduleSync);

		if (typeof ResizeObserver !== 'undefined') {
			const resizeObserver = new ResizeObserver(scheduleSync);

			items.forEach((item) => {
				const dropdown = item.querySelector('.pmm-mega-dropdown');

				if (dropdown) {
					resizeObserver.observe(dropdown);
				}
			});
		}

		if (typeof DESKTOP_MQ.addEventListener === 'function') {
			DESKTOP_MQ.addEventListener('change', () => {
				items.forEach((item) => closeItem(item));
				scheduleSync();
			});
		}
	}

	static bindMobileDrawer(headerMain) {
		const bar = headerMain.querySelector('.header-main__bar');
		const drawer = headerMain.querySelector('.header-main__drawer');
		const toggle = headerMain.querySelector('.header-main__mobile-toggle');
		const closeBtn = headerMain.querySelector('.header-main__mobile-close');

		if (!bar || !drawer || !toggle || !closeBtn) {
			return;
		}

		let previouslyFocused = null;

		const syncDrawerHidden = () => {
			const hidden = MOBILE_MQ.matches && !headerMain.classList.contains('is-drawer-open');
			if (hidden) {
				drawer.setAttribute('aria-hidden', 'true');
				drawer.inert = true;
			} else {
				drawer.removeAttribute('aria-hidden');
				drawer.inert = false;
			}
		};

		const getFocusable = () => Array.from(headerMain.querySelectorAll(FOCUSABLE_SELECTOR))
			.filter((el) => !el.hasAttribute('disabled') && isVisible(el));

		// getBoundingClientRect().bottom (not offsetHeight) — includes the WP
		// admin bar's offset above the theme header.
		const measureBarHeight = () => {
			headerMain.style.setProperty('--header-bar-height', `${bar.getBoundingClientRect().bottom}px`);
		};

		const open = () => {
			previouslyFocused = document.activeElement;
			measureBarHeight();
			headerMain.classList.add('is-drawer-open');
			document.body.classList.add('pmm-mobile-open');
			syncDrawerHidden();
			drawer.setAttribute('role', 'dialog');
			drawer.setAttribute('aria-modal', 'true');
			drawer.setAttribute('aria-label', 'Site navigation');
			toggle.setAttribute('aria-expanded', 'true');
			closeBtn.focus();
		};

		const close = () => {
			headerMain.classList.remove('is-drawer-open');
			document.body.classList.remove('pmm-mobile-open');
			syncDrawerHidden();
			drawer.removeAttribute('role');
			drawer.removeAttribute('aria-modal');
			drawer.removeAttribute('aria-label');
			toggle.setAttribute('aria-expanded', 'false');
			(previouslyFocused && typeof previouslyFocused.focus === 'function' ? previouslyFocused : toggle).focus();
		};

		// The Bars/X icons are <figure> blocks, so Enter/Space activation is manual.
		const activate = (fn) => (event) => {
			if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
			event.preventDefault();
			fn();
		};

		if (!drawer.id) drawer.id = 'header-main__drawer';
		syncDrawerHidden();
		toggle.setAttribute('aria-expanded', 'false');
		toggle.setAttribute('aria-controls', drawer.id);
		toggle.setAttribute('aria-label', 'Open menu');
		closeBtn.setAttribute('aria-label', 'Close menu');
		[toggle, closeBtn].forEach((el) => {
			el.setAttribute('role', 'button');
			el.setAttribute('tabindex', '0');
		});

		toggle.addEventListener('click', activate(open));
		toggle.addEventListener('keydown', activate(open));
		closeBtn.addEventListener('click', activate(close));
		closeBtn.addEventListener('keydown', activate(close));

		document.addEventListener('keydown', (event) => {
			if (!headerMain.classList.contains('is-drawer-open')) return;

			if (event.key === 'Escape') {
				event.preventDefault();
				close();
				return;
			}

			if (event.key !== 'Tab') return;

			const focusables = getFocusable();
			if (!focusables.length) return;

			const first = focusables[0];
			const last = focusables[focusables.length - 1];

			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		});

		const handleMQ = () => {
			if (!MOBILE_MQ.matches && headerMain.classList.contains('is-drawer-open')) {
				close();
			}
			syncDrawerHidden();
		};
		if (typeof MOBILE_MQ.addEventListener === 'function') {
			MOBILE_MQ.addEventListener('change', handleMQ);
		} else if (typeof MOBILE_MQ.addListener === 'function') {
			MOBILE_MQ.addListener(handleMQ);
		}

		window.addEventListener('resize', measureBarHeight);
		window.addEventListener('load', measureBarHeight);
		if (typeof ResizeObserver !== 'undefined') {
			new ResizeObserver(measureBarHeight).observe(bar);
		}
		measureBarHeight();
	}
}
